import React, { useEffect, useMemo, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
} from "../../../components/Component";
import { Button, Card, Input, Spinner } from "reactstrap";
import swal from "sweetalert";
import http from "../../../utils/http-common";
import { canManageAdmins } from "../../../utils/adminPermissions";

const ZONES = [
  { id: "finance", label: "Finance" },
  { id: "activity", label: "Activité" },
  { id: "directory", label: "Répertoire" },
  { id: "configuration", label: "Configuration" },
];
const SENSITIVE = [
  { id: "refund", label: "Remboursement" },
  { id: "ban_delete", label: "Bannir/supprimer" },
  { id: "api_keys", label: "Clés API" },
  { id: "manage_admins", label: "Gérer les admins" },
];

const emptyForm = () => ({
  name: "",
  description: "",
  zones: {},
  sensitive: {},
});

export default function RbacRoles() {
  const me = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const allowed = canManageAdmins(me);
  const [roles, setRoles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [isSystem, setIsSystem] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await http.get("/api/v1/admin/rbac/roles");
      setRoles(res.data?.data || []);
    } catch (e) {
      swal("Error", e.response?.data?.error || "Failed to load roles", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) load();
    else setLoading(false);
  }, [allowed]);

  const pick = (role) => {
    setSelectedId(role._id);
    setIsSystem(!!role.isSystem);
    setForm({
      name: role.name || "",
      description: role.description || "",
      zones: role.summary?.zones || {},
      sensitive: role.summary?.sensitive || {},
    });
  };

  const createNew = () => {
    setSelectedId(null);
    setIsSystem(false);
    setForm(emptyForm());
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim(),
        zones: form.zones,
        sensitive: form.sensitive,
      };
      if (selectedId) {
        await http.put(`/api/v1/admin/rbac/roles/${selectedId}`, body);
      } else {
        const res = await http.post("/api/v1/admin/rbac/roles", body);
        setSelectedId(res.data?.data?._id);
      }
      swal("Saved", "Role saved", "success");
      await load();
    } catch (e) {
      swal("Error", e.response?.data?.error || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selectedId) return;
    const ok = await swal({
      title: "Delete role?",
      text: "Blocked if the role is still assigned.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!ok) return;
    try {
      await http.delete(`/api/v1/admin/rbac/roles/${selectedId}`);
      setSelectedId(null);
      setForm(emptyForm());
      await load();
    } catch (e) {
      swal("Error", e.response?.data?.error || "Delete failed", "error");
    }
  };

  if (!allowed) {
    return (
      <>
        <Head title="Roles" />
        <Content>
          <Block>
            <p className="text-soft">You do not have permission to manage roles.</p>
          </Block>
        </Content>
      </>
    );
  }

  return (
    <>
      <Head title="Roles" />
      <Content>
        <BlockHead>
          <BlockHeadContent>
            <BlockTitle page>Roles</BlockTitle>
            <BlockDes>
              <p>Reusable permission packs assigned to admins.</p>
            </BlockDes>
          </BlockHeadContent>
          <Button color="primary" onClick={createNew}>
            + New role
          </Button>
        </BlockHead>

        {loading ? (
          <Spinner />
        ) : (
          <div className="d-flex flex-wrap" style={{ gap: 16 }}>
            <Card className="card-bordered" style={{ width: 230 }}>
              {roles.map((r) => (
                <div
                  key={r._id}
                  role="button"
                  tabIndex={0}
                  className={`px-3 py-3 border-bottom ${
                    selectedId === r._id ? "bg-primary-dim" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => pick(r)}
                  onKeyDown={(e) => e.key === "Enter" && pick(r)}
                >
                  <strong className="d-block">{r.name}</strong>
                  <span className="text-soft small">
                    {r.adminCount || 0} admin{r.adminCount === 1 ? "" : "s"}
                    {r.isSystem ? " · system" : ""}
                  </span>
                </div>
              ))}
            </Card>
            <Card className="card-bordered p-3 flex-grow-1" style={{ minWidth: 280 }}>
              <Input
                className="mb-2"
                placeholder="Role name"
                value={form.name}
                disabled={isSystem}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
              <Input
                className="mb-3"
                placeholder="Description"
                value={form.description}
                disabled={isSystem}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
              <div className="text-uppercase text-soft small font-weight-bold mb-2">
                Zones · view / act
              </div>
              {ZONES.map((z) => (
                <div
                  key={z.id}
                  className="d-flex align-items-center justify-content-between py-1"
                  style={{ gap: 16 }}
                >
                  <span>{z.label}</span>
                  <span className="small text-soft">
                    <label className="mr-3 mb-0">
                      <input
                        type="checkbox"
                        className="mr-1"
                        disabled={isSystem}
                        checked={!!form.zones[z.id]?.view}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            zones: {
                              ...f.zones,
                              [z.id]: { ...f.zones[z.id], view: e.target.checked },
                            },
                          }))
                        }
                      />
                      view
                    </label>
                    <label className="mb-0">
                      <input
                        type="checkbox"
                        className="mr-1"
                        disabled={isSystem}
                        checked={!!form.zones[z.id]?.act}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            zones: {
                              ...f.zones,
                              [z.id]: { ...f.zones[z.id], act: e.target.checked },
                            },
                          }))
                        }
                      />
                      act
                    </label>
                  </span>
                </div>
              ))}
              <div className="text-uppercase text-danger small font-weight-bold mt-3 mb-2">
                Sensitive actions
              </div>
              <div className="d-flex flex-wrap" style={{ gap: "8px 18px" }}>
                {SENSITIVE.map((s) => (
                  <label key={s.id} className="mb-0">
                    <input
                      type="checkbox"
                      className="mr-1"
                      disabled={isSystem}
                      checked={!!form.sensitive[s.id]}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          sensitive: { ...f.sensitive, [s.id]: e.target.checked },
                        }))
                      }
                    />
                    {s.label}
                  </label>
                ))}
              </div>
              <div className="d-flex justify-content-between align-items-center mt-4">
                <Button
                  color="link"
                  className="text-danger p-0"
                  disabled={!selectedId || isSystem}
                  onClick={remove}
                >
                  Delete role
                </Button>
                <Button color="primary" disabled={saving || isSystem || !form.name.trim()} onClick={save}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              </div>
              {isSystem ? (
                <p className="text-soft small mt-2 mb-0">System roles cannot be edited or deleted.</p>
              ) : null}
            </Card>
          </div>
        )}
      </Content>
    </>
  );
}
